import { Fragment, useState } from 'react';

import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import { useTranslations } from 'next-intl';

export default function ConfirmationDialog({
  title,
  description,
  shouldOpen,
  btnTextOnProceed,
  btnTextOnCancel,
  closeModalOnBackdropClick = true,
  onProceed,
  onCancel,
  onClosed,
}: {
  title: string;
  description: string;
  shouldOpen: boolean;
  btnTextOnProceed?: string;
  btnTextOnCancel?: string;
  closeModalOnBackdropClick?: boolean;
  onProceed?: () => void;
  onCancel?: () => void;
  onClosed?: () => void;
}) {
  const tCommon = useTranslations('common');

  const [isOpen, setIsOpen] = useState(shouldOpen);

  function handleProceed() {
    setIsOpen(false);
    onProceed && onProceed();
  }

  function handleCancel() {
    setIsOpen(false);
    onCancel && onCancel();
  }

  const handleCloseModal = () => {
    if (closeModalOnBackdropClick) {
      setIsOpen(false);
      onClosed && onClosed();
    }
  };

  return (
    <>
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={handleCloseModal}>
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/25" />
          </TransitionChild>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <TransitionChild
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <DialogPanel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  {title && (
                    <DialogTitle as="h3" className="text-lg font-medium leading-6 text-gray-900">
                      {title}
                    </DialogTitle>
                  )}

                  <div className="mt-2">
                    <p className="text-sm text-gray-500">{description}</p>
                  </div>

                  <div className="mt-4">
                    {onProceed && (
                      <button
                        type="button"
                        className="inline-flex justify-center rounded-md border border-transparent bg-blue-100 px-4 py-2 text-sm font-medium text-blue-900 hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 mr-2"
                        onClick={handleProceed}
                      >
                        {btnTextOnProceed || tCommon('proceed')}
                      </button>
                    )}
                    {onCancel && (
                      <button
                        type="button"
                        className="inline-flex justify-center rounded-md border border-transparent bg-blue-100 px-4 py-2 text-sm font-medium text-blue-900 hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                        onClick={handleCancel}
                      >
                        {btnTextOnCancel || tCommon('cancel')}
                      </button>
                    )}
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}
